import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';

/**
 * One canonical Rechnungsintervall option as returned by GET /v5/bmd/intervals.
 * Mirrors config/rechnung_intervall.php.
 */
export interface RechnungIntervallOption {
  type: string;
  label: string;
  interval_months?: number;
  anchor_month?: number | null;
  run_month_offset?: number;
}

export interface BmdBillingResult {
  result: number;
  error?: string;
  data?: any;
  // preview / single-run shapes surface the built invoice + positions
  rechnung?: any;
  positions?: any[];
  order_xml?: string;
  [key: string]: any;
}

@Injectable({ providedIn: 'root' })
export class BmdService {
  private apiUrl = environment.apiUrl;

  constructor(private http: HttpClient) {}

  /** Rechnungsintervall dropdown options (4 canonical types). */
  getIntervals(): Observable<{ result: number; data: RechnungIntervallOption[] }> {
    return this.http.get<{ result: number; data: RechnungIntervallOption[] }>(`${this.apiUrl}/bmd/intervals`);
  }

  /** Voransicht: dry-run of the invoice + BMD ORDER XML for one Standort. */
  preview(payload: { standort_id: string; mode?: string; year?: number; index?: number }): Observable<BmdBillingResult> {
    return this.http.post<BmdBillingResult>(`${this.apiUrl}/bmd/preview`, payload);
  }

  /** Initial pro-rata invoice for a client that started mid-period. */
  billingInitial(payload: { standort_id: string; dry_run?: boolean; year?: number; index?: number }): Observable<BmdBillingResult> {
    return this.http.post<BmdBillingResult>(`${this.apiUrl}/bmd/billing/initial`, payload);
  }

  /** Final pro-rata invoice for a client that cancelled mid-period. */
  billingFinal(payload: { standort_id: string; dry_run?: boolean; year?: number; index?: number }): Observable<BmdBillingResult> {
    return this.http.post<BmdBillingResult>(`${this.apiUrl}/bmd/billing/final`, payload);
  }

  /** Trigger a regular batch billing run for an interval + period. */
  billingRun(payload: { type?: string; year?: number; index?: number; dry_run?: boolean; standort_ids?: string[] }): Observable<BmdBillingResult> {
    return this.http.post<BmdBillingResult>(`${this.apiUrl}/bmd/billing/run`, payload);
  }

  /** Trigger the BMD ORDER export. */
  export(payload: { dry_run?: boolean; no_upload?: boolean; limit?: number; rechnung_ids?: string[] }): Observable<BmdBillingResult> {
    return this.http.post<BmdBillingResult>(`${this.apiUrl}/bmd/export`, payload);
  }

  /** Evidenz journal list, optionally filtered by standort. */
  journalList(filter: { standort?: string; kunde?: string; hausverwaltung?: string; status?: string; limit?: number } = {}): Observable<{ result: number; data: any[] }> {
    const params: any = {};
    Object.entries(filter).forEach(([k, v]) => { if (v !== undefined && v !== null && v !== '') params[k] = v; });
    return this.http.get<{ result: number; data: any[] }>(`${this.apiUrl}/bmd/journal`, { params });
  }

  /** Absolute URL for the print-as-PDF view of a journal entry. */
  journalPdfUrl(id: string): string {
    return `${this.apiUrl}/bmd/journal/${id}/pdf`;
  }

  /** Open (not yet entered in BMD) master-data changes, grouped by entity type. */
  getChangelog(entityType?: string): Observable<{ result: number; data: { total: number; groups: { entity_type: string; label: string; items: any[] }[] } }> {
    const params: any = entityType ? { entity_type: entityType } : {};
    return this.http.get<any>(`${this.apiUrl}/bmd/changelog`, { params });
  }

  /** Mark master-data changes as entered in BMD. */
  markChangelogEntered(ids: string[]): Observable<{ result: number; updated?: number; error?: string }> {
    return this.http.post<{ result: number; updated?: number; error?: string }>(`${this.apiUrl}/bmd/changelog/mark-entered`, { ids });
  }
}
